import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Icon from "../components/Icon";
import { BtnPrimary } from "../components/Atoms";
import {
  colorUsuario, semanasDelAnio, numeroSemana, sumarDias, lunesDe,
  fmt, isoLocal, esFinde, tramoGuardia, festivosDeTramo, planificarGuardias,
} from "../utils";
import Campo from "../components/forms/Campo";
import { clicable } from "../components/ClicableTeclado.jsx";

const MONTHS_ES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];
const DAYS_INI = ["L","M","X","J","V","S","D"];

function gridMes(year, month) {
  const primero = new Date(year, month, 1);
  const offset  = (primero.getDay() + 6) % 7;
  const total   = new Date(year, month + 1, 0).getDate();
  const celdas  = [];
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let d = 1; d <= total; d++) celdas.push(isoLocal(new Date(year, month, d)));
  while (celdas.length % 7 !== 0) celdas.push(null);
  return celdas;
}

export default function GuardiasView({
  guardias = [], users = [], teams = [], vacaciones = [], festivos = {}, currentUser,
  year, setYear, isAdmin = false,
  onAsignar, onAplicarPlan, onLimpiar, onImportar,
}) {
  const [panelRotar, setPanelRotar] = useState(false);
  // Brocha: la persona activa se "pinta" al pulsar una semana.
  // null = sin brocha, "" = liberar.
  const [brocha, setBrocha] = useState(null);
  // Semana cuyo desplegable de personas esta abierto: { semana, rect }.
  const [menu, setMenu] = useState(null);
  const hoy = isoLocal(new Date());
  // Limites de la semana en curso (lunes a domingo), para remarcarla en el
  // cuadrante: el recuadro del dia solo dice que hoy es hoy, y lo que se
  // reparte aqui son semanas enteras.
  const lunesActual   = lunesDe(hoy);
  const domingoActual = sumarDias(lunesActual, 6);
  const anioActual = new Date().getFullYear();
  const esAnioActual = year === anioActual;

  // Al entrar en Guardias interesa ver quién está y quién entra, no el año que
  // se dejó abierto en otra pestaña: se vuelve al año en curso y se desplaza la
  // vista al mes de hoy.
  const mesRefs = useRef({});
  useEffect(() => {
    if (year !== anioActual) setYear(anioActual);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!esAnioActual) return;
    const el = mesRefs.current[new Date().getMonth()];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [esAnioActual, year]);

  const semanas = useMemo(() => semanasDelAnio(year), [year]);

  const porSemana = useMemo(() => {
    const m = {};
    guardias.forEach(g => { m[g.semana_inicio] = g; });
    return m;
  }, [guardias]);

  // Tramo real de cada semana. El relevo se hace en persona, así que solo cabe en
  // día hábil: si el lunes es festivo, la guardia anterior se prolonga y esta
  // empieza más tarde. Se incluyen las semanas vecinas para que los bordes de
  // enero y diciembre queden bien pintados.
  const tramos = useMemo(() => {
    const lista = [sumarDias(semanas[0], -7), ...semanas, sumarDias(semanas[semanas.length-1], 7)];
    return lista.map(lunes => ({ lunes, ...tramoGuardia(lunes, festivos) }));
  }, [semanas, festivos]);

  // día → { semana, guardia }. Pintar por tramo y no por semana natural es lo que
  // hace visible la regla: un lunes festivo sale con el color de quien sigue
  // cubriendo, no con el de quien entra.
  const porDia = useMemo(() => {
    const m = {};
    tramos.forEach(t => {
      const g = porSemana[t.lunes];
      let d = t.inicio;
      for (let i = 0; d <= t.fin && i < 20; i++) {
        m[d] = { semana: t.lunes, guardia: g, inicio: t.inicio };
        d = sumarDias(d, 1);
      }
    });
    return m;
  }, [tramos, porSemana]);

  // Quién está de guardia hoy y quién entra después. Se prueba también la semana
  // anterior porque, si el lunes fue festivo, hoy puede caer todavía en el tramo
  // de quien venía cubriendo.
  const turnoAhora = useMemo(() => {
    const candidatas = [lunesDe(hoy), sumarDias(lunesDe(hoy), -7)];
    for (const lunes of candidatas) {
      const t = tramoGuardia(lunes, festivos);
      if (hoy >= t.inicio && hoy <= t.fin) return { lunes, ...t };
    }
    return null;
  }, [hoy, festivos]);

  const turnoSiguiente = useMemo(() => {
    if (!turnoAhora) return null;
    const lunes = sumarDias(turnoAhora.lunes, 7);
    return { lunes, ...tramoGuardia(lunes, festivos) };
  }, [turnoAhora, festivos]);

  // Recuento anual por persona, sobre los tramos reales
  const resumen = useMemo(() => {
    const m = {};
    semanas.forEach(lunes => {
      const g = porSemana[lunes];
      if (!g?.user_id) return;
      const k = String(g.user_id);
      const u = m[k] || (m[k] = { user_id:g.user_id, name:g.user_name, semanas:0, festivos:0, dias:0 });
      const t = tramoGuardia(lunes, festivos);
      u.semanas++;
      u.dias     += t.dias;
      u.festivos += festivosDeTramo(lunes, festivos).length;
    });
    return Object.values(m).sort((a,b) => b.semanas - a.semanas);
  }, [semanas, porSemana, festivos]); // eslint-disable-line

  const medias = useMemo(() => {
    if (!resumen.length) return { semanas:0, festivos:0 };
    const n = resumen.length;
    return {
      semanas:  resumen.reduce((s,r) => s + r.semanas,  0) / n,
      festivos: resumen.reduce((s,r) => s + r.festivos, 0) / n,
    };
  }, [resumen]);

  const sinAsignar = semanas.filter(s => !porSemana[s]?.user_id).length;
  const usuariosOrdenados = [...users].sort((a,b) => (a.name||"").localeCompare(b.name||""));

  // Las guardias las hace el equipo de TIC. Se suma quien ya tenga alguna semana
  // asignada, que es como siguen apareciendo los ex-TIC que aún entran en el
  // reparto: cuando dejan de tener semanas, desaparecen solos de la lista.
  const conGuardia = useMemo(() => new Set(resumen.map(r => String(r.user_id))), [resumen]);

  const equipoGuardias = useMemo(() => {
    const tic = teams.find(t => (t.name || "").trim().toUpperCase() === "TIC");
    const s = new Set(conGuardia);
    (tic?.members || []).forEach(id => s.add(String(id)));
    // Sin equipo TIC ni guardias no habría a quién asignar: mejor toda la lista
    return s.size ? s : new Set(users.map(u => String(u.id)));
  }, [teams, conGuardia, users]);

  // Personas apartadas del reparto a mano. Se guardan en el navegador: es una
  // preferencia de quien organiza el cuadrante, no un dato del equipo.
  const [fuera, setFuera] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("guardias_fuera") || "[]")); }
    catch { return new Set(); }
  });
  const toggleFuera = id => setFuera(p => {
    const n = new Set(p); const k = String(id);
    if (n.has(k)) n.delete(k); else n.add(k);
    try { localStorage.setItem("guardias_fuera", JSON.stringify([...n])); } catch {}
    return n;
  });

  // Primero quienes ya hacen guardias, para que la paleta empiece por lo habitual
  const candidatos = usuariosOrdenados.filter(u => equipoGuardias.has(String(u.id)));
  const brochas = [
    ...candidatos.filter(u =>  conGuardia.has(String(u.id))),
    ...candidatos.filter(u => !conGuardia.has(String(u.id))),
  ];
  // El color se reparte entre el equipo de guardias, no entre todos los usuarios:
  // sobre el total, cinco personas pueden caer en tonos casi iguales.
  const paleta = brochas;

  // Los apartados siguen visibles para poder readmitirlos, pero no entran en el
  // reparto automático.
  const participantes = brochas.filter(u => !fuera.has(String(u.id)));

  // Vacaciones por persona, para avisar en el desplegable de quien no deberia
  // coger esa semana. Mismo criterio que el planificador: solape de rangos.
  const ausencias = useMemo(() => {
    const m = {};
    vacaciones.forEach(v => {
      (m[String(v.user_id)] = m[String(v.user_id)] || []).push({
        desde: String(v.fecha).slice(0,10),
        hasta: String(v.fecha_fin || v.fecha).slice(0,10),
        tipo:  v.tipo || "vacaciones",
      });
    });
    return m;
  }, [vacaciones]);
  // Devuelve el tipo de ausencia que solapa el tramo, o null. Interesa el tipo y
  // no un si/no: no es lo mismo cambiarle la guardia a quien esta de baja.
  const ausenciaEn = (userId, desde, hasta) =>
    (ausencias[String(userId)] || []).find(a => a.desde <= hasta && a.hasta >= desde)?.tipo || null;

  // Recalcular el cuadrante: reparte semanas y festivos a la vez y salta a quien
  // este de vacaciones. El plan se calcula aqui y el backend solo lo guarda.
  const planificar = (opts = {}) => planificarGuardias({
    semanas, participantes: participantes.map(u => u.id),
    festivos, vacaciones,
    asignadas: Object.fromEntries(
      Object.entries(porSemana).filter(([, g]) => g?.user_id).map(([k, g]) => [k, g.user_id])),
    previo: porSemana[sumarDias(semanas[0], -7)]?.user_id || null,
    sobrescribir: true, equilibrarFestivos: true,
    ...opts,
  });

  const recalcular = async () => {
    if (!participantes.length) return;
    const plan = planificar();
    const aviso = `Se reasignaran ${plan.asignaciones.length} semanas de ${year} entre `
      + `${participantes.length} persona(s), equilibrando festivos y saltando a quien `
      + `este de vacaciones.`
      + (plan.sinCubrir ? ` ${plan.sinCubrir} semana(s) no tienen a nadie libre.` : "")
      + " Se pierden las asignaciones manuales del anio. Continuar?";
    if (!window.confirm(aviso)) return;
    await onAplicarPlan?.(plan, `${year}-01-01`, `${year}-12-31`);
  };

  // Nombre de la persona activa en la brocha, para el aviso de "estas asignando".
  const nombreBrocha = brocha
    ? (brochas.find(u => String(u.id) === String(brocha))
       || users.find(u => String(u.id) === String(brocha)))?.name || "esa persona"
    : "";

  // Los apartados del reparto no ensucian el desplegable. La excepcion es quien
  // ya lleva esa semana: si no saliera, no se veria a quien hay que relevar.
  const personasMenu = actual => {
    const lista = [...participantes];
    if (actual && !lista.some(u => String(u.id) === String(actual))) {
      const u = brochas.find(x => String(x.id) === String(actual))
             || users.find(x => String(x.id) === String(actual));
      if (u) lista.unshift(u);
    }
    return lista;
  };

  // Pulsar un dia reparte la semana entera: la del tramo que pinta ese dia, que
  // con un lunes festivo no es la semana natural. Sin brocha no hace nada, y
  // volver a pulsar la semana de quien ya la tiene la libera.
  const clickDia = (iso, ev) => {
    if (!isAdmin) return;
    const semana = porDia[iso]?.semana || lunesDe(iso);
    const actual = porSemana[semana]?.user_id;
    // Sin brocha activa el clic abre el desplegable de esa semana: cambiar una
    // guardia sola es lo que mas se hace y asi no obliga a elegir persona antes.
    if (brocha === null) {
      const r = ev?.currentTarget?.getBoundingClientRect();
      setMenu({ semana, rect: r ? { left:r.left, right:r.right, top:r.top, bottom:r.bottom } : null });
      return;
    }
    if (brocha === "") {
      if (actual) onAsignar?.(semana, null);
      return;
    }
    if (String(actual) === String(brocha)) onAsignar?.(semana, null);
    else onAsignar?.(semana, brocha);
  };

  return (
    <div className="side-split" style={{display:"flex",gap:16,flex:1,minHeight:0}}>

      {/* ── Panel lateral ── */}
      <aside className="side-stack" style={{width:235,flexShrink:0,display:"flex",flexDirection:"column",gap:12,overflowY:"auto"}}>

        {isAdmin && (
          <div style={cajaS}>
            <div style={{...tituloS,marginBottom:6}}>Asignar</div>
            <p style={{fontSize:11,color:"var(--text3)",lineHeight:1.45,margin:"0 0 9px"}}>
              Pulsa cualquier día y elige a quién le toca esa semana. Para repartir
              varias seguidas, elige antes una persona aquí y ve pulsando semanas.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:210,overflowY:"auto"}}>
              {brochas.map(u => {
                const on   = String(brocha) === String(u.id);
                const off  = fuera.has(String(u.id));
                const c    = colorUsuario(u.id, paleta);
                return (
                  <div key={u.id} style={{display:"flex",alignItems:"center",gap:4}}>
                    <button className="btn" onClick={()=>setBrocha(on ? null : String(u.id))}
                      style={{flex:1,minWidth:0,padding:"4px 10px",borderRadius:20,cursor:"pointer",
                        fontSize:11,fontFamily:"var(--font)",textAlign:"left",
                        border:`1px solid ${on?c:"var(--border)"}`, background:on?c:"var(--surface2)",
                        color:on?"#fff":"var(--text2)", fontWeight:on?700:500,
                        opacity: off && !on ? .45 : 1,
                        display:"flex",alignItems:"center",gap:5}}>
                      {!on && <span style={{width:8,height:8,borderRadius:"50%",background:c}}/>}
                      <span style={{flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
                        textDecoration: off ? "line-through" : "none"}}>{u.name}</span>
                    </button>
                    <button aria-label={off ? "Volver a incluir en el reparto" : "Apartar del reparto automático"} className="btn" onClick={()=>toggleFuera(u.id)}
                      title={off ? "Volver a incluir en el reparto" : "Apartar del reparto automático"}
                      style={{padding:"3px 5px",borderRadius:6,cursor:"pointer",display:"flex",flexShrink:0,
                        border:"1px solid var(--border)",background:"var(--surface)"}}>
                      <Icon name={off ? "plus-lg" : "x"} size={10} color={off ? "var(--ok-text)" : "var(--text3)"}/>
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:7,lineHeight:1.4}}>
              {participantes.length} de {brochas.length} entran en el reparto automático.
              Los tachados se pueden seguir asignando a mano.
            </div>
            <button className="btn" onClick={()=>setBrocha(brocha === "" ? null : "")}
              style={{...botonS, marginTop:8,
                borderColor: brocha === "" ? "var(--red)" : "var(--border)",
                color: brocha === "" ? "var(--red-text)" : "var(--text2)"}}>
              <Icon name="trash3" size={12} color={brocha === "" ? "var(--red-text)" : "var(--text2)"}/>
              Liberar semanas
            </button>
            {brocha !== null && (
              <button className="btn" onClick={()=>setBrocha(null)}
                style={{...botonS, marginTop:5, borderStyle:"dashed"}}>
                Terminar de asignar
              </button>
            )}
          </div>
        )}

        <div style={cajaS}>
          <div style={tituloS}>Reparto {year}</div>
          {resumen.length ? (
            <>
              <div style={{display:"flex",fontSize:11,color:"var(--text3)",fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",paddingBottom:5,borderBottom:"1px solid var(--border2)",marginBottom:4}}>
                <span style={{flex:1}}>Persona</span>
                <span style={{width:26,textAlign:"right"}} title="Semanas de guardia">Sem</span>
                <span style={{width:26,textAlign:"right"}} title="Días cubiertos, incluidas las prórrogas por festivo en lunes">Días</span>
                <span style={{width:26,textAlign:"right"}} title="Festivos que caen dentro de su guardia">Fes</span>
              </div>
              {resumen.map(r => {
                const dS = r.semanas  - medias.semanas;
                const dF = r.festivos - medias.festivos;
                const col = v => Math.abs(v) < 0.5 ? "var(--text)" : v > 0 ? "var(--red)" : "var(--green)";
                return (
                  <div key={r.user_id} style={{display:"flex",alignItems:"center",gap:5,padding:"4px 0",fontSize:11.5}}>
                    <span style={{width:9,height:9,borderRadius:3,background:colorUsuario(r.user_id,paleta),flexShrink:0}}/>
                    <span style={{flex:1,color:"var(--text2)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
                    <b style={{width:26,textAlign:"right",color:col(dS)}}>{r.semanas}</b>
                    <span style={{width:26,textAlign:"right",color:"var(--text3)"}}>{r.dias}</span>
                    <b style={{width:26,textAlign:"right",color:col(dF)}}>{r.festivos}</b>
                  </div>
                );
              })}
              <div style={{fontSize:11,color:"var(--text3)",marginTop:8,lineHeight:1.45}}>
                Media: {medias.semanas.toFixed(1)} semanas y {medias.festivos.toFixed(1)} festivos.
                En <span style={{color:"var(--red-text)"}}>rojo</span> quien va por encima,
                en <span style={{color:"var(--ok-text)"}}>verde</span> por debajo.
              </div>
            </>
          ) : <div style={{fontSize:11,color:"var(--text3)"}}>Ninguna semana asignada todavía.</div>}
        </div>

        {sinAsignar > 0 && (
          <div style={{...cajaS,borderColor:"var(--warn-line)",background:"var(--warn-bg)"}}>
            <div style={{display:"flex",alignItems:"center",gap:7,fontSize:11.5,color:"var(--warn-text)"}}>
              <Icon name="exclamation-triangle-fill" size={12} color="#a16207"/>
              {sinAsignar} semana{sinAsignar===1?"":"s"} sin cubrir
            </div>
          </div>
        )}

        {isAdmin && (
          <div style={cajaS}>
            <div style={tituloS}>Cuadrante</div>
            <button className="btn" onClick={recalcular} disabled={!participantes.length}
              title="Reparte el año entero entre los participantes, saltando a quien esté de vacaciones"
              style={{...botonS, borderColor:"var(--accent)", color:"var(--accent)",
                background:"var(--accent-bg)", opacity: participantes.length ? 1 : .5}}>
              <Icon name="arrow-repeat" size={12} color="var(--accent)"/> Recalcular {year}
            </button>
            <button className="btn" onClick={()=>setPanelRotar(p=>!p)}
              style={{...botonS, marginTop:6,
                background:panelRotar?"var(--accent-bg)":"var(--surface2)",
                color:panelRotar?"var(--accent)":"var(--text2)"}}>
              <Icon name="arrow-repeat" size={12} color={panelRotar?"var(--accent)":"var(--text2)"}/> Repartir por turnos
            </button>
            <button className="btn" onClick={onImportar} style={{...botonS, marginTop:6}}>
              <Icon name="download" size={12} color="var(--text2)"/> Importar desde Excel
            </button>
            <button className="btn" onClick={()=>{
                if (window.confirm(`¿Vaciar todas las guardias de ${year}?`))
                  onLimpiar?.(semanas[0], semanas[semanas.length-1]);
              }}
              style={{...botonS, marginTop:6, color:"var(--red-text)"}}>
              <Icon name="trash3" size={12} color="var(--red-text)"/> Vaciar {year}
            </button>
          </div>
        )}
      </aside>

      {/* ── Año ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:12,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button aria-label="Año anterior" onClick={()=>setYear(y=>y-1)} className="btn" style={navBtnS}>
            <Icon name="chevron-left" size={14} color="var(--text2)"/>
          </button>
          <div style={{flex:1,textAlign:"center"}}>
            <div style={{fontWeight:800,fontSize:16,color:"var(--text)",letterSpacing:"-.02em"}}>Guardias {year}</div>
            <div className="m-hide" style={{fontSize:11,color:"var(--text3)",marginTop:1}}>
              {semanas.length} semanas · {semanas.length - sinAsignar} cubiertas
            </div>
          </div>
          <button aria-label="Año siguiente" onClick={()=>setYear(y=>y+1)} className="btn" style={navBtnS}>
            <Icon name="chevron-right" size={14} color="var(--text2)"/>
          </button>
        </div>

        {/* ── Quién está y quién entra ── */}
        {esAnioActual && turnoAhora && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[["De guardia ahora", turnoAhora, true], ["Entra después", turnoSiguiente, false]]
              .filter(([, t]) => t)
              .map(([label, t, activo]) => {
                const g   = porSemana[t.lunes];
                const col = g?.user_id ? colorUsuario(g.user_id, paleta) : "var(--border)";
                const fes = festivosDeTramo(t.lunes, festivos);
                return (
                  <div key={label} style={{
                    flex: "1 1 220px", minWidth: 0, display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 13px", borderRadius: 11,
                    background: activo ? "var(--surface)" : "var(--surface2)",
                    border: `1px solid ${activo ? col : "var(--border)"}`,
                    borderLeft: `4px solid ${col}`,
                    boxShadow: activo ? "var(--shadow-sm)" : "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".08em" }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: g?.user_name ? "var(--text)" : "var(--text3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {g?.user_name || "Sin asignar"}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>
                        {fmt(t.inicio)} – {fmt(t.fin)} · {t.dias} días
                        {fes.length > 0 && (
                          <span style={{ color: "var(--red-text)", fontWeight: 700 }}>
                            {" · "}{fes.length} festivo{fes.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Los tramos de más de 7 días son los que se han prolongado por festivo */}
                    {t.dias !== 7 && (
                      <span title={t.dias > 7
                          ? "El relevo se retrasó porque el lunes era festivo"
                          : "Este tramo empieza más tarde porque su lunes era festivo"}
                        style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 20, flexShrink: 0,
                          background: "rgba(239,68,68,.10)", color: "var(--red-text)" }}>
                        {t.dias > 7 ? `+${t.dias - 7}d` : `${t.dias - 7}d`}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {/* Sin este aviso no se entiende por qué al pulsar un día cambia algo */}
        {isAdmin && brocha !== null && (
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",borderRadius:9,
            background:"var(--accent-bg)",border:"1px solid var(--accent)",fontSize:11.5,color:"var(--text2)"}}>
            <Icon name="pencil" size={12} color="var(--accent)"/>
            {brocha === ""
              ? <span>Pulsa una semana para <b>liberarla</b>.</span>
              : <span>Pulsa una semana para asignarla a <b>{nombreBrocha}</b>.</span>}
            <span style={{flex:1}}/>
            <button aria-label="Quitar la brocha" className="btn" onClick={()=>setBrocha(null)}
              style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex",background:"none",border:"none",cursor:"pointer",display:"flex",padding:2}}>
              <Icon name="x" size={13} color="var(--text3)"/>
            </button>
          </div>
        )}

        {panelRotar && isAdmin && (
          <PanelRotar users={participantes} paleta={paleta} semanas={semanas}
            onCancel={()=>setPanelRotar(false)}
            onRotar={async (desde,hasta,ids,sobrescribir,equilibrar)=>{
              const rango = semanas.filter(x => x >= desde && x <= hasta);
              const plan = planificar({ semanas: rango, participantes: ids, sobrescribir,
                equilibrarFestivos: equilibrar,
                previo: porSemana[sumarDias(rango[0], -7)]?.user_id || null });
              await onAplicarPlan?.(plan, `${year}-01-01`, `${year}-12-31`);
              setPanelRotar(false);
            }}/>
        )}

        <div className="cal-anio" style={{flex:1,overflowY:"auto"}}>
          {MONTHS_ES.map((nombre, mi) => (
            <div key={mi} ref={el => { mesRefs.current[mi] = el; }}
              style={{background:"var(--surface)",borderRadius:10,padding:"9px 10px 10px",
                border: esAnioActual && mi === new Date().getMonth()
                  ? "1.5px solid var(--accent)" : "1px solid var(--border)"}}>
              <div style={{fontSize:11.5,fontWeight:800,color:"var(--text)",textAlign:"center",marginBottom:6}}>{nombre}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
                {DAYS_INI.map((d,di) => (
                  <div key={di} style={{fontSize:11,fontWeight:700,color:"var(--text3)",textAlign:"center",paddingBottom:2}}>{d}</div>
                ))}
                {gridMes(year, mi).map((iso, ci) => {
                  if (!iso) return <div key={ci}/>;
                  const dia   = parseInt(iso.slice(8), 10);
                  const info  = porDia[iso];
                  const g     = info?.guardia;
                  const col   = g?.user_id ? colorUsuario(g.user_id, paleta) : null;
                  const fest  = festivos[iso];
                  const finde = esFinde(iso);
                  // Sin nadie asignado no hay relevo que marcar: en un año vacío
                  // saldría una raya en todos los lunes sin significar nada.
                  const esRelevo = !!g?.user_id && iso === info?.inicio;
                  const enSemana = iso >= lunesActual && iso <= domingoActual;

                  return (
                    <div key={ci} {...clicable(e=>clickDia(iso, e), { activo: isAdmin })}
                      title={[
                        enSemana && "Semana actual",
                        fest && `Festivo: ${fest}`,
                        g?.user_name ? `Guardia: ${g.user_name}` : "Semana sin asignar",
                        esRelevo && "Relevo este día",
                      ].filter(Boolean).join("\n")}
                      style={{
                        aspectRatio:"1", display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize: fest ? 10.5 : 9.5,
                        fontWeight: fest || iso===hoy ? 800 : 500,
                        borderRadius:4,
                        background: col || (finde ? "var(--bg)" : "transparent"),
                        // Un festivo se raya en rojo sobre el color de quien está de
                        // guardia: así se ve el festivo sin perder de vista a quién
                        // le toca cubrirlo, que es justo lo que hay que compensar.
                        backgroundImage: fest
                          ? "repeating-linear-gradient(45deg, rgba(239,68,68,.92) 0 4px, rgba(239,68,68,.55) 4px 8px)"
                          : "none",
                        color: fest ? "#fff" : col ? "#fff" : finde ? "var(--text3)" : "var(--text2)",
                        textShadow: fest ? "0 1px 2px rgba(0,0,0,.55)" : "none",
                        cursor: isAdmin ? "pointer" : "default",
                        border: iso===hoy ? "1.5px solid var(--accent)" : "1px solid transparent",
                        boxShadow: [
                          esRelevo ? "inset 2.5px 0 0 rgba(0,0,0,.4)" : "",
                          fest ? "0 0 0 1.5px var(--red)" : "",
                          // Subrayado interior: se ve sobre el color de quien
                          // esta de guardia y no choca con el recuadro de hoy ni
                          // con la marca de relevo, que van por otros lados.
                          enSemana ? "inset 0 -3px 0 var(--accent)" : "",
                        ].filter(Boolean).join(",") || "none",
                        position:"relative", zIndex: fest ? 1 : 0,
                      }}>
                      {dia}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:14,fontSize:11,color:"var(--text3)",flexWrap:"wrap"}}>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:10,height:10,borderRadius:2,background:"var(--text3)",display:"inline-block",boxShadow:"inset 2.5px 0 0 rgba(0,0,0,.4)"}}/> Día del relevo
          </span>
          <span style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{width:10,height:10,borderRadius:2,background:"var(--text3)",display:"inline-block",boxShadow:"inset 0 -3px 0 var(--accent)"}}/> Semana actual
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:10,height:10,borderRadius:2,display:"inline-block",
              backgroundImage:"repeating-linear-gradient(45deg, rgba(239,68,68,.92) 0 4px, rgba(239,68,68,.55) 4px 8px)",
              boxShadow:"0 0 0 1.5px var(--red)"}}/> Festivo
          </span>
          <span>Si el lunes es festivo el relevo pasa al primer día hábil: la guardia anterior cubre un día más y la siguiente uno menos.</span>
        </div>
      </div>

      {menu && isAdmin && (
        <MenuSemana rect={menu.rect}
          tramo={tramoGuardia(menu.semana, festivos)}
          festivosTramo={festivosDeTramo(menu.semana, festivos)}
          actual={porSemana[menu.semana]?.user_id || null}
          personas={personasMenu(porSemana[menu.semana]?.user_id)}
          paleta={paleta} ausenciaEn={ausenciaEn}
          onElegir={id => { setMenu(null); onAsignar?.(menu.semana, id); }}
          onCerrar={() => setMenu(null)}/>
      )}
    </div>
  );
}

// Desplegable de una semana: quien la lleva y a quien pasarsela, sin salir del
// cuadrante. Va en el body y en position:fixed porque el anio tiene su propio
// scroll y dentro de la celda del dia se recortaria.
function MenuSemana({ rect, tramo, festivosTramo = [], actual, personas = [], paleta = [],
                      ausenciaEn = () => null, onElegir, onCerrar }) {
  const caja = useRef(null);
  const ANCHO = 226;

  useEffect(() => {
    const fueraDeCaja = e => { if (caja.current && !caja.current.contains(e.target)) onCerrar(); };
    const tecla = e => { if (e.key === "Escape") onCerrar(); };
    // Al desplazar, el menu fijo dejaria de acompanar a su semana: se cierra.
    document.addEventListener("mousedown", fueraDeCaja);
    document.addEventListener("keydown", tecla);
    // Salvo el de la propia lista, que no mueve el menu de sitio.
    const desplazar = e => { if (!caja.current?.contains(e.target)) onCerrar(); };
    window.addEventListener("scroll", desplazar, true);
    window.addEventListener("resize", onCerrar);
    return () => {
      document.removeEventListener("mousedown", fueraDeCaja);
      document.removeEventListener("keydown", tecla);
      window.removeEventListener("scroll", desplazar, true);
      window.removeEventListener("resize", onCerrar);
    };
  }, [onCerrar]);

  const ALTO = 300;
  const r    = rect || { left: window.innerWidth/2 - ANCHO/2, top: window.innerHeight/2, bottom: window.innerHeight/2 };
  const abajo = window.innerHeight - r.bottom > ALTO || r.top < ALTO;
  const pos  = {
    left: Math.max(8, Math.min(r.left - 6, window.innerWidth - ANCHO - 8)),
    ...(abajo ? { top: r.bottom + 6 } : { bottom: window.innerHeight - r.top + 6 }),
  };

  const fila = (contenido, onClick, activa) => (
    <div {...clicable(onClick, { label: typeof contenido === "string" ? contenido : undefined })}
      style={{display:"flex",alignItems:"center",gap:7,padding:"7px 11px",cursor:"pointer",
        background: activa ? "var(--accent-bg)" : "transparent"}}
      onMouseEnter={e => e.currentTarget.style.background = "var(--bg)"}
      onMouseLeave={e => e.currentTarget.style.background = activa ? "var(--accent-bg)" : "transparent"}>
      {contenido}
    </div>
  );

  return createPortal(
    <div ref={caja}
      style={{position:"fixed",...pos,zIndex:500,width:ANCHO,background:"var(--surface)",
        border:"1px solid var(--border)",borderRadius:11,boxShadow:"var(--shadow-lg)",overflow:"hidden"}}>

      <div style={{padding:"8px 11px",borderBottom:"1px solid var(--border2)",background:"var(--surface2)"}}>
        <div style={{fontSize:11.5,fontWeight:700,color:"var(--text)"}}>
          {fmt(tramo.inicio)} – {fmt(tramo.fin)}
        </div>
        <div style={{fontSize:11,color:"var(--text3)",marginTop:1}}>
          {tramo.dias} días
          {festivosTramo.length ? " · " + festivosTramo.length + (festivosTramo.length > 1 ? " festivos" : " festivo") : ""}
        </div>
      </div>

      <div style={{maxHeight:250,overflowY:"auto"}}>
        {personas.map(u => {
          const activa = String(u.id) === String(actual);
          const falta  = ausenciaEn(u.id, tramo.inicio, tramo.fin);
          return (
            <div key={u.id}>
              {fila(<>
                <span style={{width:9,height:9,borderRadius:"50%",flexShrink:0,
                  background: colorUsuario(u.id, paleta)}}/>
                <span style={{flex:1,minWidth:0,fontSize:12,whiteSpace:"nowrap",
                  overflow:"hidden",textOverflow:"ellipsis",fontWeight: activa ? 700 : 500,
                  color: falta ? "var(--text3)" : "var(--text)",
                  textDecoration: falta ? "line-through" : "none"}}>{u.name}</span>
                {/* Asignarle la semana igualmente no se impide, pero se avisa del motivo */}
                {falta && <span title={"Esa semana: " + falta}
                  style={{fontSize:11,color:"var(--red-text)",fontWeight:700}}>{ETIQUETA_AUSENCIA[falta] || falta}</span>}
                {activa && <Icon name="check2" size={12} color="var(--accent)"/>}
              </>, () => onElegir(activa ? null : u.id), activa)}
            </div>
          );
        })}
        {!personas.length && (
          <div style={{padding:"10px 11px",fontSize:11.5,color:"var(--text3)"}}>
            No hay nadie en el equipo de guardias.
          </div>
        )}
      </div>

      {actual && (
        <div style={{borderTop:"1px solid var(--border2)"}}>
          {fila(<>
            <Icon name="trash3" size={11} color="var(--red-text)"/>
            <span style={{fontSize:11.5,color:"var(--red-text)",fontWeight:600}}>Liberar la semana</span>
          </>, () => onElegir(null), false)}
        </div>
      )}
    </div>,
    document.body);
}

function PanelRotar({ users, paleta = [], semanas, onRotar, onCancel }) {
  const [seleccion,    setSeleccion   ] = useState(() => new Set());
  const [desde,        setDesde       ] = useState(semanas[0]);
  const [hasta,        setHasta       ] = useState(semanas[semanas.length-1]);
  const [sobrescribir, setSobrescribir] = useState(false);
  const [equilibrar,   setEquilibrar  ] = useState(true);
  const [busy,         setBusy        ] = useState(false);

  const toggle = id => setSeleccion(p => {
    const n = new Set(p); const k = String(id);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  // El orden de la rotación es el de la lista mostrada, no el de selección
  const ids = users.filter(u => seleccion.has(String(u.id))).map(u => u.id);
  const valid = ids.length > 0 && desde <= hasta;

  const run = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try { await onRotar(desde, hasta, ids, sobrescribir, equilibrar); } finally { setBusy(false); }
  };

  return (
    <div style={{background:"var(--surface2)",borderRadius:11,border:"1px solid var(--border)",padding:14}}>
      <div style={{...tituloS,marginBottom:6}}>Repartir por turnos</div>
      <p style={{fontSize:11,color:"var(--text3)",lineHeight:1.5,margin:"0 0 10px"}}>
        Se asignan las semanas del rango rotando entre las personas marcadas, en el orden
        de la lista, saltando a quien esté de vacaciones esa semana y procurando que a todos
        les toquen los mismos festivos. La rotación continúa donde la dejó la última semana
        ya asignada antes del rango, así que ampliar el cuadrante no reinicia el turno.
      </p>

      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
        {users.map(u => {
          const on = seleccion.has(String(u.id));
          const c  = colorUsuario(u.id, paleta);
          return (
            <button key={u.id} className="btn" onClick={()=>toggle(u.id)}
              style={{padding:"5px 11px",borderRadius:20,cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",
                border:`1px solid ${on?c:"var(--border)"}`, background:on?c+"1f":"var(--surface)",
                color:on?"var(--text)":"var(--text3)", fontWeight:on?700:500,
                display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:on?c:"var(--border)"}}/>
              {u.name}
            </button>
          );
        })}
      </div>

      <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap",marginBottom:10}}>
        <div style={{flex:1,minWidth:130}}>
          <Campo><label style={lbS}>Desde la semana</label>
          <select value={desde} onChange={e=>setDesde(e.target.value)} style={inS}>
            {semanas.map(s => <option key={s} value={s}>{numeroSemana(s)} · {fmt(s)}</option>)}
          </select></Campo>
        </div>
        <div style={{flex:1,minWidth:130}}>
          <Campo><label style={lbS}>Hasta la semana</label>
          <select value={hasta} onChange={e=>setHasta(e.target.value)} style={inS}>
            {semanas.map(s => <option key={s} value={s}>{numeroSemana(s)} · {fmt(s)}</option>)}
          </select></Campo>
        </div>
      </div>

      <label style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:"var(--text2)",marginBottom:7}}>
        <input type="checkbox" checked={sobrescribir} onChange={e=>setSobrescribir(e.target.checked)}
          style={{accentColor:"var(--accent)",width:14,height:14}}/>
        Sobrescribir las semanas ya asignadas a mano
      </label>
      <label style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:"var(--text2)",marginBottom:12}}
        title="Las semanas con festivo van a quien menos festivos lleve, en vez de al siguiente del turno">
        <input type="checkbox" checked={equilibrar} onChange={e=>setEquilibrar(e.target.checked)}
          style={{accentColor:"var(--accent)",width:14,height:14}}/>
        Equilibrar también los festivos
      </label>

      <div style={{display:"flex",gap:8,justifyContent:"flex-end",alignItems:"center"}}>
        {!ids.length && <span style={{flex:1,fontSize:11,color:"var(--text3)"}}>Marca al menos una persona.</span>}
        <button className="btn" onClick={onCancel}
          style={{padding:"7px 14px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:12.5,background:"var(--surface)",color:"var(--text2)",fontFamily:"var(--font)"}}>
          Cancelar
        </button>
        <BtnPrimary onClick={run} disabled={!valid || busy}>{busy ? "Repartiendo…" : "Repartir"}</BtnPrimary>
      </div>
    </div>
  );
}

const ETIQUETA_AUSENCIA = { vacaciones:"vac.", permiso:"permiso", baja:"baja" };

const cajaS   = {background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:"13px 13px 11px"};
const tituloS = {fontWeight:700,fontSize:11,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:9};
const navBtnS = {padding:"5px 10px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",cursor:"pointer",display:"flex",alignItems:"center"};
const botonS  = {width:"100%",padding:"6px 8px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",cursor:"pointer",fontSize:11.5,fontWeight:600,color:"var(--text2)",fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:6,justifyContent:"center"};
const lbS     = {fontSize:11,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em"};
const inS     = {width:"100%",padding:"7px 9px",borderRadius:8,border:"1px solid var(--border)",fontSize:12,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface)",color:"var(--text)"};
